'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import ValidationMessage from './ValidationMessage';
import HeroHeader from '@/components/common/HeroHeader';

// --- 1. DEFINITIONS & INTERFACES ---

interface FilterOption {
  id: string;
  label: string;
  value: string;
  comingSoon?: boolean;
}

interface ConfigurationState {
  tenses: string[];
  categories: string[];
  pronouns: string[];
  difficulty: string;
  duration: string;
}

// --- 2. INTERNAL COMPONENT: ACCORDION ROW ---

interface AccordionRowProps {
  title: string;
  summary: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const AccordionRow = ({ title, summary, isOpen, onToggle, children }: AccordionRowProps) => {
  return (
    <div
      className={`
        relative overflow-hidden mb-3 border transition-all duration-300 ease-in-out rounded-xl
        ${isOpen
          ? 'border-[#8C7B6B]/30 bg-white shadow-md'
          : 'border-[#E2DAD0] bg-white hover:bg-[#FDFCFA] hover:border-[#C4BAB0] shadow-sm'
        }
      `}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 text-left focus:outline-none focus:ring-2 focus:ring-[#8C7B6B]/30 focus:ring-offset-1 rounded-xl group"
      >
        <div className="flex flex-col gap-1">
          <span className={`text-sm font-medium uppercase tracking-[0.1em] transition-colors ${isOpen ? 'text-[#1A1614]' : 'text-[#2A2420]'}`}>
            {title}
          </span>
          <span className="text-xs text-[#6B6259] truncate max-w-[240px] md:max-w-md font-light tracking-wide">
            {summary || 'Ninguno seleccionado'}
          </span>
        </div>

        <div className={`text-[#6B6259] transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#8C7B6B]' : ''}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6"/>
          </svg>
        </div>
      </button>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-5 pb-5 pt-0">
          <div className="w-full h-px bg-[#E8E2D8] mb-4" />
          <div className="space-y-2">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- 3. MAIN COMPONENT ---

const DrillConfigurationInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const [openSection, setOpenSection] = useState<string | null>('tenses');

  const [configuration, setConfiguration] = useState<ConfigurationState>({
    tenses: [],
    categories: [],
    pronouns: [],
    difficulty: '',
    duration: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // --- DATA OPTIONS ---
  const tenseOptions: FilterOption[] = [
    { id: 'challenge', label: 'Desafío Irregular (Mix de Tiempos)', value: 'Challenge' },
    { id: 'present-simple', label: 'Presente Simple', value: 'Present Simple' },
    { id: 'present-continuous', label: 'Presente Continuo', value: 'Present Continuous' },
    { id: 'past-simple', label: 'Pasado Simple', value: 'Past Simple' },
    { id: 'past-continuous', label: 'Pasado Continuo', value: 'Past Continuous' },
    { id: 'present-perfect', label: 'Presente Perfecto', value: 'Present Perfect' },
    { id: 'past-perfect', label: 'Pasado Perfecto', value: 'Past Perfect', comingSoon: true },
    { id: 'future-simple', label: 'Futuro Simple', value: 'Future Simple', comingSoon: true },
    { id: 'future-continuous', label: 'Futuro Continuo', value: 'Future Continuous', comingSoon: true }
  ];

  const regularTenseValues = ['Present Simple', 'Present Continuous', 'Past Simple', 'Past Continuous', 'Present Perfect'];

  const categoryOptions: FilterOption[] = [
    { id: 'regular', label: 'Verbos Regulares', value: 'Regular' },
    { id: 'irregular', label: 'Verbos Irregulares', value: 'Irregular' }
  ];

  const pronounOptions: FilterOption[] = [
    { id: 'i-you', label: 'I / You', value: 'i,you' },
    { id: 'he-she-it', label: 'He / She / It', value: 'he,she,it' },
    { id: 'we-they', label: 'We / They', value: 'we,they' }
  ];

  // --- HELPERS ---

  const toggleSection = (id: string) => {
    setOpenSection(openSection === id ? null : id);
  };

  const getSummary = (selectedValues: string[], allOptions: FilterOption[]) => {
    if (selectedValues.length === 0) return 'Ninguno seleccionado';
    if (selectedValues.length === allOptions.length) return 'Todos seleccionados';
    const labels = selectedValues.map(val => allOptions.find(o => o.value === val)?.label || val);
    return labels.join(', ');
  };

  const handleCheckboxChange = (field: keyof ConfigurationState, value: string) => {
    setConfiguration((prev) => {
      const currentList = prev[field] as string[];

      if (field === 'tenses') {
        const isCurrentlySelected = currentList.includes(value);

        if (value === 'Challenge') {
          return {
            ...prev,
            tenses: isCurrentlySelected ? [] : ['Challenge'],
            categories: isCurrentlySelected ? [] : ['Irregular'],
            difficulty: isCurrentlySelected ? prev.difficulty : 'Challenge'
          };
        }

        const listWithoutChallenge = currentList.filter(t => t !== 'Challenge');
        const newList = isCurrentlySelected
          ? listWithoutChallenge.filter(item => item !== value)
          : [...listWithoutChallenge, value];

        const anyRegularSelected = newList.some(t => regularTenseValues.includes(t));

        return {
          ...prev,
          tenses: newList,
          categories: anyRegularSelected ? ['Regular'] : [],
          difficulty: anyRegularSelected ? '' : prev.difficulty
        };
      }

      const valuesToToggle = value.split(',');
      const allPresent = valuesToToggle.every(v => currentList.includes(v));

      const newList = allPresent
        ? currentList.filter(item => !valuesToToggle.includes(item))
        : [...new Set([...currentList, ...valuesToToggle])];

      return { ...prev, [field]: newList };
    });
  };

  const toggleAll = (field: keyof ConfigurationState, allOptions: FilterOption[]) => {
    const currentList = configuration[field] as string[];

    const selectableValues = allOptions
      .filter(o => !o.comingSoon)
      .map(o => o.value)
      .filter(val => val !== 'Challenge')
      .flatMap(val => val.split(','));

    if (currentList.length === selectableValues.length) {
      setConfiguration({
        ...configuration,
        [field]: [],
        categories: []
      });
    } else {
      setConfiguration({
        ...configuration,
        [field]: selectableValues,
        categories: ['Regular']
      });
    }
  };

  // --- VALIDATION & SUBMIT ---

  const validateConfiguration = (): boolean => {
    if (configuration.tenses.length === 0) { setValidationError('Selecciona al menos un tiempo verbal.'); return false; }
    if (configuration.categories.length === 0) { setValidationError('Selecciona al menos una categoría.'); return false; }
    if (configuration.pronouns.length === 0) { setValidationError('Selecciona al menos un pronombre.'); return false; }
    if (!configuration.duration) { setValidationError('Selecciona la duración.'); return false; }
    setValidationError('');
    return true;
  };

  const handleStartDrill = () => {
    if (!isHydrated) return;
    if (validateConfiguration()) {
      if (typeof window !== 'undefined') {
        localStorage.setItem('drillConfiguration', JSON.stringify(configuration));
        router.push('/exercise-interface');
      }
    }
  };

  const isFormValid =
    configuration.tenses.length > 0 &&
    configuration.categories.length > 0 &&
    configuration.pronouns.length > 0 &&
    configuration.duration !== '';

  const isChallengeActive = configuration.tenses.includes('Challenge');
  const isRegularTenseActive = configuration.tenses.some(t => regularTenseValues.includes(t));
  const isCategoryLocked = isChallengeActive || isRegularTenseActive;

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-[#F2EDE4] pb-12">
        <HeroHeader
          title="Cargando..."
          subtitle="XAVIER EVAN - CURSO DE INGLÉS"
          backgroundImageUrl=""
        />
      </div>
    );
  }

  return (
    <div className="page-transition-wrap min-h-screen bg-[#F2EDE4] pb-20">

      <HeroHeader
        title="Conjugación de Verbos"
        subtitle="Xavier Evan — Curso de Inglés"
        backgroundImageUrl=""
      />

      <div className="max-w-lg mx-auto px-4 mt-10">

        {/* --- SECTION 1: TENSES --- */}
        <AccordionRow
          title="Tiempos Verbales"
          summary={getSummary(configuration.tenses, tenseOptions)}
          isOpen={openSection === 'tenses'}
          onToggle={() => toggleSection('tenses')}
        >
          <div className="flex justify-end mb-2">
            <button
              onClick={() => !isChallengeActive && toggleAll('tenses', tenseOptions)}
              className={`text-[10px] font-medium uppercase tracking-[0.15em] transition-colors ${
                isChallengeActive
                  ? 'text-[#C4BAB0] cursor-not-allowed'
                  : 'text-[#6B6259] hover:text-[#1A1614]'
              }`}
            >
              {configuration.tenses.filter(t => regularTenseValues.includes(t)).length === regularTenseValues.length ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
            </button>
          </div>

          {/* Desafío Irregular row */}
          <div className="grid grid-cols-1 gap-1 mb-3">
            {tenseOptions.filter(o => o.value === 'Challenge').map((option) => (
              <label
                key={option.id}
                className="flex items-center space-x-3 p-2 hover:bg-[#F5F0E8] rounded-lg cursor-pointer group transition-colors"
              >
                <div className="relative flex items-center flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={configuration.tenses.includes(option.value)}
                    onChange={() => handleCheckboxChange('tenses', option.value)}
                    className="peer appearance-none w-4 h-4 border border-[#C4BAB0] rounded checked:bg-[#8C7B6B] checked:border-[#8C7B6B] transition-all cursor-pointer"
                  />
                  <svg className="absolute w-2.5 h-2.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 left-[3px] top-[3px]" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5L4.5 8.5L11 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="text-sm text-[#4A4440] group-hover:text-[#1A1614] transition-colors font-light">{option.label}</span>
              </label>
            ))}
          </div>

          {/* Divider + Regular label */}
          <div className="w-full h-px bg-[#E8E2D8] mb-3" />
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[#6B6259] mb-2 px-1">
            Verbos Regulares
          </p>

          {/* Regular tenses */}
          <div className="grid grid-cols-1 gap-0.5">
            {tenseOptions.filter(o => o.value !== 'Challenge').map((option) => {
              const isThisDisabled = isChallengeActive || !!option.comingSoon;

              return (
                <label
                  key={option.id}
                  className={`flex items-center space-x-3 p-2 rounded-lg transition-colors
                    ${isThisDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#F5F0E8] cursor-pointer group'}`}
                >
                  <div className="relative flex items-center flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={configuration.tenses.includes(option.value)}
                      onChange={() => !isThisDisabled && handleCheckboxChange('tenses', option.value)}
                      disabled={isThisDisabled}
                      className="peer appearance-none w-4 h-4 border border-[#C4BAB0] rounded checked:bg-[#8C7B6B] checked:border-[#8C7B6B] transition-all disabled:border-[#D8D0C4] cursor-pointer disabled:cursor-not-allowed"
                    />
                    <svg className="absolute w-2.5 h-2.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 left-[3px] top-[3px]" viewBox="0 0 12 10" fill="none">
                      <path d="M1 5L4.5 8.5L11 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className={`text-sm font-light transition-colors ${isThisDisabled ? 'text-[#C4BAB0]' : 'text-[#4A4440] group-hover:text-[#1A1614]'}`}>
                    {option.label}
                  </span>
                  {option.comingSoon && (
                    <span className="ml-auto text-[9px] font-medium uppercase tracking-[0.15em] text-[#6B6259] border border-[#8C7B6B]/25 rounded-full px-2 py-0.5">
                      Próximamente
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </AccordionRow>

        {/* --- SECTION 2: CATEGORIES --- */}
        <AccordionRow
          title="Categorías"
          summary={getSummary(configuration.categories, categoryOptions)}
          isOpen={openSection === 'categories'}
          onToggle={() => toggleSection('categories')}
        >
          <div className="space-y-0.5">
            {categoryOptions.map((option) => (
              <label
                key={option.id}
                className={`flex items-center space-x-3 p-2 rounded-lg transition-colors
                  ${isCategoryLocked ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#F5F0E8] cursor-pointer group'}`}
              >
                <div className="relative flex items-center flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={configuration.categories.includes(option.value)}
                    onChange={() => !isCategoryLocked && handleCheckboxChange('categories', option.value)}
                    disabled={isCategoryLocked}
                    className="peer appearance-none w-4 h-4 border border-[#C4BAB0] rounded checked:bg-[#8C7B6B] checked:border-[#8C7B6B] transition-all disabled:border-[#D8D0C4] cursor-pointer disabled:cursor-not-allowed"
                  />
                  <svg className="absolute w-2.5 h-2.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 left-[3px] top-[3px]" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5L4.5 8.5L11 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className={`text-sm font-light transition-colors ${isCategoryLocked ? 'text-[#C4BAB0]' : 'text-[#4A4440] group-hover:text-[#1A1614]'}`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
          {isCategoryLocked && (
            <p className="text-[10px] text-[#6B6259] mt-3 px-1 tracking-wide">
              {isChallengeActive
                ? '⚙️ Categoría fijada automáticamente: Verbos Irregulares' : '⚙️ Categoría fijada automáticamente: Verbos Regulares'}
            </p>
          )}
        </AccordionRow>

        {/* --- SECTION 3: PRONOUNS --- */}
        <AccordionRow
          title="Pronombres"
          summary={getSummary(configuration.pronouns, pronounOptions)}
          isOpen={openSection === 'pronouns'}
          onToggle={() => toggleSection('pronouns')}
        >
          <div className="flex justify-end mb-2">
            <button onClick={() => toggleAll('pronouns', pronounOptions)} className="text-[10px] text-[#6B6259] font-medium uppercase tracking-[0.15em] hover:text-[#1A1614] transition-colors">
              {configuration.pronouns.length === pronounOptions.length ? 'Ninguno' : 'Todos'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {pronounOptions.map((option) => (
              <label key={option.id} className="flex items-center space-x-3 p-2 hover:bg-[#F5F0E8] rounded-lg cursor-pointer group transition-colors">
                <div className="relative flex items-center flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={option.value.split(',').every(v => configuration.pronouns.includes(v))}
                    onChange={() => handleCheckboxChange('pronouns', option.value)}
                    className="peer appearance-none w-4 h-4 border border-[#C4BAB0] rounded checked:bg-[#8C7B6B] checked:border-[#8C7B6B] transition-all cursor-pointer"
                  />
                  <svg className="absolute w-2.5 h-2.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 left-[3px] top-[3px]" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <span className="text-sm font-light text-[#4A4440] group-hover:text-[#1A1614] transition-colors">{option.label}</span>
              </label>
            ))}
          </div>
        </AccordionRow>

        {/* --- SECTION 4: PREFERENCES --- */}
        <AccordionRow
          title="Opciones"
          summary={configuration.duration ? `${configuration.duration} preguntas` : 'Selecciona opciones'}
          isOpen={openSection === 'options'}
          onToggle={() => toggleSection('options')}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-[#6B6259] uppercase tracking-[0.2em] mb-3">Duración</label>
              <select
                value={configuration.duration}
                onChange={(e) => setConfiguration({...configuration, duration: e.target.value})}
                className="w-full bg-white border border-[#D8D0C4] text-[#1A1614] p-3 text-sm font-light rounded-lg focus:ring-2 focus:ring-[#8C7B6B]/30 focus:border-[#8C7B6B] focus:outline-none transition-all appearance-none cursor-pointer"
              >
                <option value="" disabled>Seleccionar</option>
                <option value="10">10 Preguntas</option>
                <option value="25">25 Preguntas</option>
                <option value="50">50 Preguntas</option>
                <option value="100">100 Preguntas</option>
              </select>
            </div>
          </div>
        </AccordionRow>

        {/* --- VALIDATION ERROR --- */}
        {validationError && (
          <div className="mt-4">
            <ValidationMessage message={validationError} type="error" />
          </div>
        )}

        {/* --- START BUTTON --- */}
        <div className="flex justify-center pt-10 mb-12">
          <button
            onClick={handleStartDrill}
            disabled={!isFormValid}
            className="
              w-full md:w-auto md:min-w-[300px]
              group relative px-8 py-4 bg-[#1A1614] text-white font-medium
              rounded-xl shadow-md hover:shadow-lg hover:bg-[#2A2420]
              transform hover:-translate-y-px transition-all duration-300
              disabled:opacity-30 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
              flex items-center justify-center gap-3 tracking-[0.15em] text-xs uppercase
            "
          >
            <span>Comenzar Práctica</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </button>
        </div>

      </div>
    </div>
  );
};

export default DrillConfigurationInteractive;
